// https://zustand.docs.pmnd.rs/guides/connect-to-state-with-url-hash#persist-and-connect-state-with-url-parameters-(example:-url-query-parameters)

import type { NoInfer, OmitFunctions } from '@seen/core/types'
import { omitBy } from 'lodash-es'
import type { StateStorage } from 'zustand/middleware'

const getUrlSearch = (): string => window.location.search.slice(1)

export const createURLPersistentStorage = <
  T extends Record<string, unknown> = never,
>({
  omitKeys,
}: {
  omitKeys?: (keyof OmitFunctions<NoInfer<T>>)[]
} = {}): StateStorage => {
  const omittedKeysMap = Object.fromEntries(
    omitKeys?.map((key) => [key, true]) ?? []
  ) as Record<keyof OmitFunctions<T>, true>

  return {
    getItem: (key): string | null => {
      const searchParams = new URLSearchParams(getUrlSearch())
      return searchParams.get(key)
    },
    setItem: (key, _newValue): void => {
      const searchParams = new URLSearchParams(getUrlSearch())

      try {
        const newValue = JSON.parse(_newValue)
        // Only include state properties, not functions
        newValue.state = omitBy(
          newValue.state,
          (_value, k) => k in omittedKeysMap || typeof _value === 'function'
        )

        searchParams.set(key, JSON.stringify(newValue))
        window.history.replaceState(null, '', `?${searchParams.toString()}`)
      } catch (error) {
        console.error('Error updating URL with state:', error)
      }
    },
    removeItem: (key): void => {
      const searchParams = new URLSearchParams(getUrlSearch())
      searchParams.delete(key)
      // Use replaceState instead of directly setting location.search to avoid page reload
      window.history.replaceState(
        null,
        '',
        searchParams.toString()
          ? `?${searchParams.toString()}`
          : window.location.pathname
      )
    },
  }
}

export const createStoreQueryParamBuilder =
  <T extends Record<string, unknown>>(storeName: string) =>
  (state: OmitFunctions<T>, version = 0) => {
    const searchParams = new URLSearchParams()

    const zustandStoreParams = {
      state,
      version, // version is here because that is included with how Zustand sets the state
    }

    searchParams.set(storeName, JSON.stringify(zustandStoreParams))
    return searchParams.toString()
  }
